import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { User, Clock, ArrowLeft, Edit, Trash2, AlertCircle, CreditCard, ExternalLink, Loader2, ShieldAlert, Share2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Avatar } from '../components/Profile/Avatar';
import { Username } from '../components/Profile/Username';
import { BookmarkButton } from '../components/BookmarkButton';
import { EventForm } from '../components/Listings/Forms/EventForm';
import { TicketPurchaseModal } from '../components/Events/TicketPurchaseModal';
import { TicketSalesOverview } from '../components/Events/TicketSalesOverview';
import { createConnectAccount, getConnectStatus, requestEventDeletion } from '../lib/stripe/connect';
import { ImageGalleryModal } from '../components/ui/ImageGalleryModal';
import { Meta } from '../components/Meta';
import { Schema } from '../components/Schema';
import { LocationMap } from '../components/LocationMap';
import { formatDate, formatTime } from '../lib/utils/dateUtils';
import { VIBE_COLORS, DEFAULT_VIBES } from '../types/vibes';
import type { SubCategory } from '../types/vibes';
import type { Event } from '../types/events';
import { useTranslation } from 'react-i18next';

export function EventDetails() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOwnEvent, setIsOwnEvent] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [connectStatus, setConnectStatus] = useState<string | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [stockInfo, setStockInfo] = useState<Record<string, { total: number; sold: number }>>({});
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [attendees, setAttendees] = useState<{ id: string; avatar_url: string | null }[]>([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [instantAttendLoading, setInstantAttendLoading] = useState(false);

  const fetchStockInfo = async (eventId: string) => {
    const { data } = await supabase
      .from('ticket_tier_stock')
      .select('tier_name, total_quantity, sold_quantity')
      .eq('event_id', eventId);
    if (data) {
      const info: Record<string, { total: number; sold: number }> = {};
      data.forEach(row => {
        info[row.tier_name] = { total: row.total_quantity, sold: row.sold_quantity };
      });
      setStockInfo(info);
    }
  };

  const fetchAttendees = async (eventId: string) => {
    setAttendeesLoading(true);
    try {
      // First get ticket purchases for this event
      const { data: purchases } = await supabase
        .from('ticket_purchases')
        .select('user_id, quantity')
        .eq('event_id', eventId)
        .in('status', ['paid', 'completed']);

      if (purchases && purchases.length > 0) {
        // Get unique user IDs
        const userIds = [...new Set(purchases.map(p => p.user_id))];

        // Fetch user details
        const { data: users } = await supabase
          .from('users')
          .select('id, avatar_url')
          .in('id', userIds);

        if (users) {
          setAttendees(users);
        }
      }
    } catch (err) {
      console.error('Error fetching attendees:', err);
    } finally {
      setAttendeesLoading(false);
    }
  };

  const fetchEvent = async () => {
    if (!slug) return;
    
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          user:users (
            id,
            username,
            avatar_url,
            verified
          )
        `)
  .eq('slug', slug)
  .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('Event not found');

      setEvent(data);
      setIsOwnEvent(currentUser?.id === data.user_id);

      // Fetch real stock info from ticket_tier_stock table
      if (data.ticket_tiers && data.ticket_tiers.length > 0) {
        fetchStockInfo(data.id);
      }

      // Fetch attendees
      fetchAttendees(data.id);
    } catch (err: any) {
      console.error('Error fetching event:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [slug]);

  // Fetch subCategories for display
  useEffect(() => {
    async function fetchSubCategories() {
      const { data } = await supabase
        .from('sub_categories')
        .select('*')
        .order('display_order');
      if (data) setSubCategories(data);
    }
    fetchSubCategories();
  }, []);

  // Check Stripe Connect status for event owners with ticket sales
  useEffect(() => {
    if (isOwnEvent && event?.ticket_tiers && event.ticket_tiers.length > 0) {
      getConnectStatus().then((status) => {
        setConnectStatus(status.status || 'none');
      }).catch(() => {
        setConnectStatus('none');
      });
    }
  }, [isOwnEvent, event]);

  const handleConnectStripe = async () => {
    try {
      setConnectLoading(true);
      const result = await createConnectAccount();
      if (result.onboardingUrl) {
        window.location.href = result.onboardingUrl;
      } else if (result.status === 'active') {
        setConnectStatus('active');
      }
    } catch (err: any) {
      console.error('Error connecting Stripe:', err);
    } finally {
      setConnectLoading(false);
    }
  };

  const [deletionLoading, setDeletionLoading] = useState(false);

  const hasTicketTiers = event?.ticket_tiers && event.ticket_tiers.length > 0;
  const [hasPaidOrders, setHasPaidOrders] = useState(false);

  useEffect(() => {
    if (!event?.id || !hasTicketTiers) return;
    (async () => {
      const { count } = await supabase
        .from('ticket_orders')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', event.id)
        .in('status', ['paid', 'processing']);
      setHasPaidOrders((count ?? 0) > 0);
    })();
  }, [event?.id, hasTicketTiers]);

  // Only go through cancellation+refund flow if there are actual paid orders
  const hasTicketSales = hasTicketTiers && hasPaidOrders;

  const handleDelete = async () => {
    if (!event?.id) return;

    if (hasTicketSales) {
      // Event has ticket sales — request cancellation (admin must approve)
      const confirmed = window.confirm(
        'This event has ticket sales. A cancellation request will be sent to the site admin for review. Once approved, all ticket buyers will be refunded.\n\nAre you sure you want to request cancellation?'
      );
      if (!confirmed) return;

      try {
        setDeletionLoading(true);
        const result = await requestEventDeletion({
          eventId: event.id,
          reason: 'Organizer requested cancellation',
        });

        if (result.success) {
          alert(result.message);
          if (result.action === 'deleted') {
            navigate('/events');
          } else {
            // Refresh to show "requested" state
            fetchEvent();
          }
        }
      } catch (err: any) {
        console.error('Error requesting event deletion:', err);
        alert(err.message || 'Failed to request event deletion. Please try again.');
      } finally {
        setDeletionLoading(false);
      }
    } else {
      // No ticket sales — allow direct deletion
      const confirmed = window.confirm('Are you sure you want to delete this event? This action cannot be undone.');
      if (!confirmed) return;

      try {
        const { error: deleteError } = await supabase
          .from('events')
          .delete()
          .eq('id', event.id);

        if (deleteError) throw deleteError;
        navigate('/events');
      } catch (err: any) {
        console.error('Error deleting event:', err);
        alert('Failed to delete event. Please try again.');
      }
    }
  };

  const handleStickyShare = async () => {
    if (!event) return;

    const shareData = {
      title: event.title,
      text: event.description?.slice(0, 120),
      url: window.location.href,
    };

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        // User canceled or browser blocked; fall through to clipboard.
      }
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      if (window.innerWidth < 1024) setShowShareSheet(true);
    }
  };

  const handleAttendClick = async () => {
    if (!event) return;

    const canInstantAttend =
      event.price === 0 &&
      !event.max_participants &&
      !event.total_capacity &&
      !(event.ticket_tiers && event.ticket_tiers.length > 0) &&
      !event.ticket_url;

    if (!canInstantAttend) {
      setShowTicketModal(true);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
        window.dispatchEvent(new CustomEvent('show-auth'));
        return;
      }

      setInstantAttendLoading(true);

      const { data: existingAttendance } = await supabase
        .from('ticket_purchases')
        .select('id')
        .eq('event_id', event.id)
        .eq('user_id', user.id)
        .eq('status', 'paid')
        .limit(1)
        .maybeSingle();

      if (!existingAttendance) {
        const { error: insertError } = await supabase
          .from('ticket_purchases')
          .insert({
            event_id: event.id,
            user_id: user.id,
            quantity: 1,
            total_amount: 0,
            status: 'paid',
            payment_id: `direct-attend:${event.id}:${user.id}`,
          });

        if (insertError) throw insertError;
      }

      fetchAttendees(event.id);
      alert(existingAttendance ? 'You are already attending this event.' : 'You are now attending this event.');
    } catch (err: any) {
      console.error('Error attending event:', err);
      alert(err.message || 'Could not confirm attendance. Please try again.');
    } finally {
      setInstantAttendLoading(false);
    }
  };

  const renderDescriptionWithBold = (text: string) => {
    return text.split('\n').map((line, lineIndex) => {
      const parts = line.split(/(\*[^*]+\*)/g);
      return (
        <p key={`line-${lineIndex}`} className="leading-relaxed">
          {parts.map((part, partIndex) => {
            const isBold = part.startsWith('*') && part.endsWith('*') && part.length > 2;
            if (!isBold) return <span key={`part-${lineIndex}-${partIndex}`}>{part}</span>;
            return (
              <strong key={`part-${lineIndex}-${partIndex}`} className="font-semibold text-content">
                {part.slice(1, -1)}
              </strong>
            );
          })}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-10 max-w-page mx-auto py-8">
        <Link 
          to="/events"
          className="inline-flex items-center text-accent-text hover:text-accent-text/80 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('detail.backTo', { section: t('nav.events') })}
        </Link>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-content mb-4">
            {error || t('common.noResults')}
          </h2>
          <Link 
            to="/events" 
            className="text-accent-text hover:text-accent-text/80"
          >
            {t('common.viewAll')}
          </Link>
        </div>
      </div>
    );
  }

  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  const startWeekdayShort = startDate.toLocaleDateString(undefined, { weekday: 'short' });
  const isMultiDay = startDate.toDateString() !== endDate.toDateString();
  const hasExternalTicketUrl = !!event.ticket_url;
  const hasTickets = hasTicketTiers || hasExternalTicketUrl || (event.price > 0);
  const getCurrencySymbol = (curr?: string) => {
    switch (curr) {
      case 'GBP': return '£';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'CHF': return 'CHF ';
      default: return '€';
    }
  };
  const sym = getCurrencySymbol(event.currency);

  // Check if ticket sales are currently active
  const now = new Date();
  const salesStarted = !event.sales_start_date || new Date(event.sales_start_date) <= now;
  const salesEnded = event.sales_end_date ? new Date(event.sales_end_date) < now : false;
  const eventPassed = event.start_date ? new Date(event.start_date) < now : false;
  const salesActive = salesStarted && !salesEnded && !eventPassed;
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://mindful.family';

  // Format ISO date strings for schema
  const startDateISO = new Date(event.start_date).toISOString();
  const endDateISO = new Date(event.end_date).toISOString();

  return (
    <>
      <Meta 
        title={`${event.title} - Wellness Event`}
        description={`Join us for ${event.title} on ${formatDate(event.start_date)}. ${event.description.substring(0, 150)}...`}
        image={event.image_url}
        type="event"
      />
      <Schema
        type="Event"
        data={{
          name: event.title,
          description: event.description,
          image: event.image_url || event.images?.[0],
          startDate: startDateISO,
          endDate: endDateISO,
          location: {
            name: event.location,
            address: {
              addressLocality: event.location,
              addressCountry: event.country,
            },
          },
          organizer: {
            name: event.user?.username || 'Mindful Family',
            url: event.user?.id ? `${baseUrl}/profile/${event.user.id}/listings` : baseUrl,
          },
          offers: {
            price: event.price.toString(),
            priceCurrency: (event.currency || 'EUR').toUpperCase(),
            availability: event.price === 0 ? 'https://schema.org/Free' : 'https://schema.org/InStock',
            url: event.ticket_url || `${baseUrl}/events/${event.slug}`,
          },
        }}
      />
      <div className="min-h-screen bg-background">
      <div className="w-full px-4 sm:px-6 lg:px-10 max-w-page mx-auto py-6 sm:py-8 pb-36 md:pb-32">
        <Link 
          to="/events"
          className="inline-flex items-center text-content/50 hover:text-accent-text text-sm mb-5 transition-colors min-h-[44px] min-w-[44px] py-2 -ml-2 pl-2 pr-4 rounded-lg touch-manipulation"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          {t('detail.backTo', { section: t('nav.events') })}
        </Link>

        {/* Main Content */}
        <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_390px] lg:gap-8 xl:gap-10">
          <div className="space-y-5">
          {/* Hero Image (mobile/tablet) */}
          <div className="relative rounded-2xl overflow-hidden lg:hidden">
            <div className="aspect-[16/7] sm:aspect-[16/6]">
              {event.image_url ? (
                <img
                  src={event.image_url}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{ backgroundColor: '#F4F7EE' }}
                >
                  <span
                    className="text-4xl sm:text-5xl font-semibold text-accent-text font-gelica"
                  >
                    Event
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Title, Vibe & Subcategory */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-3xl sm:text-4xl lg:text-[3rem] font-bold text-content leading-tight">{event.title}</h1>
              <button
                onClick={() => setShowShareSheet(true)}
                className="md:hidden flex-shrink-0 p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-content transition-colors"
                aria-label="Share"
              >
                <Share2 className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(event.vibe || event.primary_vibe) && (
                <span
                  className="px-3 py-1.5 text-xs font-semibold text-white rounded-full"
                  style={{ backgroundColor: event.vibe?.color || VIBE_COLORS[event.primary_vibe || ''] || '#6B7280' }}
                >
                  {event.vibe?.name || DEFAULT_VIBES.find(v => v.id === event.primary_vibe)?.name || event.primary_vibe}
                </span>
              )}
              {(event.subCategory || event.sub_category_id) && (
                <span className="px-3 py-1.5 text-xs font-semibold bg-gray-100 text-content rounded-full">
                  {event.subCategory?.name || subCategories.find(sc => sc.id === event.sub_category_id)?.name || event.sub_category_id}
                </span>
              )}
            </div>
          </div>

          {/* Organiser & Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-1">
            <div className="flex items-center gap-3">
              {event.user && (
                <Link
                  to={`/profile/${event.user.id}/listings`}
                  className="flex items-center space-x-3 group"
                >
                  <Avatar
                    url={event.user.avatar_url}
                    size="md"
                    userId={event.user.id}
                    editable={false}
                  />
                  <div>
                    <Username
                      username={event.user.username || 'Anonymous'}
                      userId={event.user.id}
                      verified={event.user.verified}
                      className="block text-sm font-semibold text-content group-hover:text-accent-text transition-colors"
                    />
                    <span className="block text-xs text-content/45">
                      {t('detail.eventOrganiser')}
                    </span>
                  </div>
                </Link>
              )}
              {/* Mobile bookmark */}
              <div className="md:hidden ml-2" onClick={(e) => e.stopPropagation()}>
                <BookmarkButton targetId={event.id} targetType="events" className="p-1.5" />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
              {isOwnEvent && event?.deletion_status !== 'cancelled' && (
                <>
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="inline-flex items-center px-3.5 py-2 text-sm font-medium text-accent-text bg-accent-text/5 hover:bg-accent-text/10 rounded-lg transition-colors"
                  >
                    <Edit className="h-3.5 w-3.5 mr-1.5" />
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deletionLoading || event?.deletion_status === 'processing' || event?.deletion_status === 'requested'}
                    className={`inline-flex items-center px-3.5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                      event?.deletion_status === 'requested'
                        ? 'text-amber-700 bg-amber-50 cursor-default'
                        : hasTicketSales
                          ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                          : 'text-red-600 bg-red-50 hover:bg-red-100'
                    }`}
                  >
                    {deletionLoading ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : event?.deletion_status === 'requested' ? (
                      <Clock className="h-3.5 w-3.5 mr-1.5" />
                    ) : hasTicketSales ? (
                      <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {deletionLoading
                      ? 'Submitting...'
                      : event?.deletion_status === 'requested'
                        ? 'Cancellation Pending Review'
                        : event?.deletion_status === 'processing'
                          ? 'Refunds in progress...'
                          : hasTicketSales
                            ? 'Request Cancellation'
                            : t('common.delete')
                    }
                  </button>
                </>
              )}
              {event?.deletion_status === 'cancelled' && (
                <div className="inline-flex items-center px-3.5 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg">
                  <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
                  Event Cancelled — All tickets refunded
                </div>
              )}
              {!isOwnEvent && (
                <div className="hidden md:block">
                  <BookmarkButton targetId={event.id} targetType="events" />
                </div>
              )}
            </div>
          </div>

          {/* Key Info Strip (mobile/tablet) */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden lg:hidden">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-100">
              {/* Date */}
              <div className="flex items-start gap-2.5 sm:gap-3.5 p-3 sm:p-5 bg-white">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                  <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/calendar-icon.png" alt="Date" className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('detail.date')}</p>
                  <p className="text-sm font-semibold text-content mt-0.5 leading-snug">
                    {isMultiDay 
                      ? `${startWeekdayShort}, ${formatDate(startDate)} – ${formatDate(endDate)}`
                      : `${startWeekdayShort}, ${formatDate(startDate)}`}
                  </p>
                </div>
              </div>

              {/* Time */}
              <div className="flex items-start gap-2.5 sm:gap-3.5 p-3 sm:p-5 bg-white">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                  <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/time-clock-icon.png" alt="Time" className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('detail.time')}</p>
                  <p className="text-sm font-semibold text-content mt-0.5">
                    {formatTime(startDate)} – {formatTime(endDate)}
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-2.5 sm:gap-3.5 p-3 sm:p-5 bg-white">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                  <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png" alt="Location" className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('detail.location')}</p>
                  <p className="text-sm font-semibold text-content mt-0.5 leading-snug">{event.location}</p>
                </div>
              </div>

              {/* Capacity */}
              <div className="flex items-start gap-2.5 sm:gap-3.5 p-3 sm:p-5 bg-white">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                  <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/users-capacity.png" alt="Capacity" className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('detail.capacity')}</p>
                  <p className="text-sm font-semibold text-content mt-0.5">
                    {event.total_capacity
                      ? event.total_capacity + ' ' + t('detail.tickets')
                      : event.max_participants 
                        ? t('detail.spots', { count: event.max_participants })
                        : t('detail.open')}
                  </p>
                  {hasTicketTiers && (
                    <p className="text-xs text-content/40 mt-0.5">
                      {t('detail.tierCount', { count: event.ticket_tiers!.length })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7">
            <h2 className="text-lg font-bold text-content mb-3">Details</h2>
            <div className="text-[15px] text-[#202020]/80 whitespace-pre-line space-y-1.5">
              {renderDescriptionWithBold(event.description)}
            </div>
          </div>

          {/* Location Map */}
          {event.latitude && event.longitude && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 z-0 relative">
              <h2 className="text-lg font-bold text-content mb-4">Location</h2>
              <LocationMap
                latitude={event.latitude}
                longitude={event.longitude}
                address={event.location}
                height="250px"
              />
            </div>
          )}

          {/* Attendees Section */}
          {(attendees.length > 0 || event.max_participants) && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center">
                    <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/users-capacity.png" alt="Attendees" className="w-6 h-6" />
                  </div>
                  <h2 className="text-lg font-bold text-content">
                    {attendees.length > 0 ? `${attendees.length} going` : 'Attendees'}
                  </h2>
                </div>
                {event.max_participants && (
                  <span className="text-xs text-content/40 bg-gray-50 px-2.5 py-1 rounded-full">
                    {event.max_participants - (event.current_participants || 0)} spots left
                  </span>
                )}
              </div>

              {/* Attendee Avatars */}
              {attendeesLoading ? (
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
                  ))}
                </div>
              ) : attendees.length > 0 ? (
                <div className="flex -space-x-2">
                  {attendees.slice(0, 8).map((attendee) => (
                    <div key={attendee.id} className="relative">
                      {attendee.avatar_url ? (
                        <img
                          src={attendee.avatar_url}
                          alt="Attendee"
                          className="w-10 h-10 rounded-full object-cover border-2 border-white"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-accent-text/20 border-2 border-white flex items-center justify-center">
                          <User className="w-5 h-5 text-accent-text" />
                        </div>
                      )}
                    </div>
                  ))}
                  {attendees.length > 8 && (
                    <div className="w-10 h-10 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-content/60">
                      +{attendees.length - 8}
                    </div>
                  )}
                </div>
              ) : event.max_participants ? (
                <p className="text-sm text-content/50">Be the first to join!</p>
              ) : null}
            </div>
          )}

          {/* Ticket Information */}
          {hasTickets && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-lg font-bold text-content">{t('detail.ticketsTitle')}</h2>
                </div>
                {event.sales_end_date && salesActive && (
                  <span className="text-xs text-content/40 bg-gray-50 px-2.5 py-1 rounded-full">
                    {t('detail.salesEndDate', { date: new Date(event.sales_end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) })}
                  </span>
                )}
              </div>

              {/* Sales status banner */}
              {!salesActive && (
                <div className="flex items-center gap-2.5 p-3.5 bg-amber-50 border border-amber-100 rounded-xl mb-4">
                  <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                  <p className="text-sm text-amber-700">
                    {eventPassed
                      ? t('detail.eventEnded')
                      : salesEnded
                        ? t('detail.ticketSalesEnded')
                        : hasExternalTicketUrl
                          ? t('detail.getTickets')
                          : t('detail.ticketSalesOpen', { date: event.sales_start_date ? new Date(event.sales_start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : t('detail.soon') })
                    }
                  </p>
                </div>
              )}

              {/* Date locked notice */}
              {hasTicketTiers && (
                <p className="text-[10px] text-content/30 mb-4 flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  Event date is fixed and cannot be changed once ticket sales are active
                </p>
              )}

              {/* Ticket Tiers (Platform) */}
              {hasTicketTiers ? (
                <div className="space-y-2.5">
                  {event.ticket_tiers!.map((tier, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-gray-50/70 border border-gray-100 rounded-xl"
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-content">{tier.name}</h3>
                        {tier.description && (
                          <p className="text-xs text-content/45 mt-0.5">{tier.description}</p>
                        )}
                        {(() => {
                          const stock = stockInfo[tier.name];
                          const remaining = stock ? stock.total - stock.sold : tier.quantity;
                          const soldOut = remaining <= 0;
                          return soldOut ? (
                            <p className="text-xs text-red-500 font-medium mt-1">Sold out</p>
                          ) : (
                            <p className="text-xs text-content/35 mt-1">{remaining} {t('detail.available')}</p>
                          );
                        })()}
                      </div>
                      <div className="text-right ml-4 shrink-0">
                        <p className="text-base font-bold text-content">{sym}{tier.price.toFixed(2)}</p>
                        {event.vat_percentage && event.vat_percentage > 0 && (
                          <p className="text-[10px] text-content/35">{t('detail.vatIncluded', { percent: event.vat_percentage })}</p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Total capacity summary */}
                  {event.total_capacity && (
                    <div className="flex items-center justify-between px-1 pt-2">
                      <span className="text-xs text-content/40">{t('detail.totalCapacity')}</span>
                      <span className="text-xs font-semibold text-content/60">{event.total_capacity} {t('detail.tickets')}</span>
                    </div>
                  )}

                  {/* Attend Button */}
                  {salesActive && !isOwnEvent && (
                    <button
                      onClick={handleAttendClick}
                      disabled={instantAttendLoading}
                      className="w-full mt-4 py-3 px-4 bg-white rounded-2xl shadow-md hover:shadow-lg active:scale-[0.98] transition-all duration-200 flex items-center justify-between border border-gray-200"
                    >
                      <div className="flex flex-col items-start">
                        <span className="px-3 py-1 rounded-full border border-gray-300 font-semibold text-sm text-gray-600">
                          {event.price === 0 ? t('common.free') : `${sym}${event.price.toFixed(2)}`}
                        </span>
                        {event.price === 0 && event.max_participants && (
                          <span className="text-xs font-medium mt-1" style={{ color: '#C4714A' }}>
                            {event.max_participants - (event.current_participants || 0)} spots left
                          </span>
                        )}
                      </div>
                      <span className="px-6 py-2 bg-[#8DA847] text-white font-semibold rounded-full">Attend</span>
                    </button>
                  )}
                </div>
              ) : hasExternalTicketUrl ? (
                /* External ticket link */
                <div className="space-y-4">
                  <div className="bg-accent-text/5 rounded-xl p-5 text-center">
                    {event.price > 0 && !eventPassed && (
                      <>
                        <p className="text-xl font-bold text-accent-text">
                          {`${sym}${event.price.toFixed(2)}`}
                        </p>
                        <p className="text-xs text-content/45 mt-1">{t('detail.perTicket')}</p>
                      </>
                    )}
                    {event.price === 0 && !eventPassed && (
                      <p className="text-lg font-bold text-accent-text">{t('common.free')}</p>
                    )}
                    {eventPassed && (
                      <p className="text-lg font-bold text-content/50">{t('detail.eventEnded')}</p>
                    )}
                  </div>
                  {!isOwnEvent && !eventPassed && (
                    <a
                      href={event.ticket_url!.startsWith('http') ? event.ticket_url! : `https://${event.ticket_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3.5 px-6 bg-accent-text text-white text-sm font-semibold rounded-2xl hover:bg-accent-text/90 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-accent-text/20 flex items-center justify-between"
                    >
                      <span className="font-semibold">Attend</span>
                      <span className="font-semibold">
                        {event.price === 0 ? t('common.free') : `${sym}${event.price.toFixed(2)}`}
                      </span>
                    </a>
                  )}
                  {!eventPassed && (
                    <p className="text-[11px] text-content/35 text-center">
                      You'll be redirected to an external website to purchase tickets.
                    </p>
                  )}
                </div>
              ) : (
                /* Fallback for old-style single price events */
                <div className="bg-accent-text/5 rounded-xl p-5 text-center">
                  <p className="text-xl font-bold text-accent-text">
                    {event.price === 0 ? t('common.free') : `${sym}${event.price.toFixed(2)}`}
                  </p>
                  <p className="text-xs text-content/45 mt-1">{t('detail.perTicket')}</p>
                </div>
              )}
            </div>
          )}

          {/* Free event with capacity but no ticket sales */}
          {!hasTickets && event.max_participants && !eventPassed && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-lg font-bold text-content">Free Event</h2>
                </div>
                <span className="text-xs text-content/40 bg-gray-50 px-2.5 py-1 rounded-full">
                  {event.max_participants - (event.current_participants || 0)} spots left
                </span>
              </div>

              {/* Attend Button for free events */}
              {salesActive && !isOwnEvent && (
                    <button
                      onClick={handleAttendClick}
                      disabled={instantAttendLoading}
                  className="w-full py-3.5 px-6 bg-accent-text text-white text-sm font-semibold rounded-2xl hover:bg-accent-text/90 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-accent-text/20 flex items-center justify-between"
                >
                  <span className="font-semibold">Attend</span>
                  <span className="font-semibold">{t('common.free')}</span>
                </button>
              )}
            </div>
          )}

          {/* Stripe Connect Warning (Owner only) */}
          {isOwnEvent && hasTicketTiers && connectStatus !== null && connectStatus !== 'active' && connectStatus !== 'under_review' && (
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5 sm:p-7">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-amber-800">
                    {connectStatus === 'pending' ? 'Complete your Stripe setup' : 'Connect Stripe to receive payments'}
                  </h3>
                  <p className="text-sm text-amber-700 mt-1">
                    {connectStatus === 'pending'
                      ? 'Your Stripe account setup is incomplete. Complete the onboarding process to start receiving ticket payments.'
                      : 'You need to connect a Stripe account before buyers can purchase tickets for this event. It takes about 2 minutes.'}
                  </p>
                  <button
                    onClick={handleConnectStripe}
                    disabled={connectLoading}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-xl hover:bg-amber-700 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {connectLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4" />
                    )}
                    {connectStatus === 'pending' ? 'Continue Setup' : 'Connect Stripe Account'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Ticket Sales Overview (Owner only) */}
          {isOwnEvent && hasTickets && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7">
              <h2 className="text-lg font-bold text-content mb-4">Ticket Sales</h2>
              <TicketSalesOverview eventId={event.id} currency={event.currency} eventEndDate={event.end_date} />
            </div>
          )}

          {/* Gallery */}
          {event.images && event.images.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7">
              <h2 className="text-lg font-bold text-content mb-4">{t('detail.gallery')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {event.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedImageIndex(index);
                      setShowImageGallery(true);
                    }}
                    className="rounded-xl w-full aspect-[4/3] overflow-hidden hover:opacity-90 transition-opacity"
                  >
                    <img
                      src={image}
                      alt={`${event.title} - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
          </div>

          {/* Desktop right column */}
          <aside className="hidden lg:block space-y-4 lg:sticky lg:top-24 self-start">
            <div className="relative rounded-2xl overflow-hidden">
              <div className="aspect-[16/10]">
                {event.image_url ? (
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ backgroundColor: '#F4F7EE' }}
                  >
                    <span className="text-5xl font-semibold text-accent-text font-gelica">
                      Event
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-1 gap-px bg-gray-100">
                {/* Date */}
                <div className="flex items-start gap-3.5 p-5 bg-white">
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                    <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/calendar-icon.png" alt="Date" className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('detail.date')}</p>
                    <p className="text-sm font-semibold text-content mt-0.5 leading-snug">
                      {isMultiDay
                        ? `${startWeekdayShort}, ${formatDate(startDate)} – ${formatDate(endDate)}`
                        : `${startWeekdayShort}, ${formatDate(startDate)}`}
                    </p>
                  </div>
                </div>

                {/* Time */}
                <div className="flex items-start gap-3.5 p-5 bg-white">
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                    <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/time-clock-icon.png" alt="Time" className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('detail.time')}</p>
                    <p className="text-sm font-semibold text-content mt-0.5">
                      {formatTime(startDate)} – {formatTime(endDate)}
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-3.5 p-5 bg-white">
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                    <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png" alt="Location" className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('detail.location')}</p>
                    <p className="text-sm font-semibold text-content mt-0.5 leading-snug">{event.location}</p>
                  </div>
                </div>

                {/* Capacity */}
                <div className="flex items-start gap-3.5 p-5 bg-white">
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                    <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/users-capacity.png" alt="Capacity" className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('detail.capacity')}</p>
                    <p className="text-sm font-semibold text-content mt-0.5">
                      {event.total_capacity
                        ? event.total_capacity + ' ' + t('detail.tickets')
                        : event.max_participants
                          ? t('detail.spots', { count: event.max_participants })
                          : t('detail.open')}
                    </p>
                    {hasTicketTiers && (
                      <p className="text-xs text-content/40 mt-0.5">
                        {t('detail.tierCount', { count: event.ticket_tiers!.length })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {showImageGallery && event.images && (
        <ImageGalleryModal
          images={event.images}
          initialIndex={selectedImageIndex}
          onClose={() => setShowImageGallery(false)}
        />
      )}

      {showEditModal && (
        <EventForm
          onClose={() => setShowEditModal(false)}
          editId={event.id}
          onSuccess={() => {
            setShowEditModal(false);
            fetchEvent();
          }}
        />
      )}

      {showTicketModal && (
        <TicketPurchaseModal
          event={{
            id: event.id,
            title: event.title,
            price: event.price,
            currency: event.currency ?? undefined,
            ticket_tiers: event.ticket_tiers ?? undefined,
            vat_percentage: event.vat_percentage ?? undefined,
            max_participants: event.max_participants ?? undefined,
            current_participants: event.current_participants ?? undefined,
            sales_start_date: event.sales_start_date ?? undefined,
            sales_end_date: event.sales_end_date ?? undefined,
          }}
          onClose={() => setShowTicketModal(false)}
          onSuccess={() => {
            setShowTicketModal(false);
            fetchEvent();
          }}
        />
      )}

      {/* Share Sheet - mobile bottom sheet */}
      {showShareSheet && createPortal(
        <div
          className="fixed inset-0 z-[70] lg:hidden"
          onClick={() => setShowShareSheet(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-8 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-6" />
            <h3 className="text-lg font-semibold text-center mb-6">Share event</h3>
            <div className="grid grid-cols-4 gap-4">
              {/* Copy Link */}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setShowShareSheet(false);
                }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <span className="text-xs text-[#202020]/70">Copy link</span>
              </button>

              {/* WhatsApp */}
              <a
                href={`https://wa.me/?text=${encodeURIComponent(event.title + ' - ' + window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2"
                onClick={() => setShowShareSheet(false)}
              >
                <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <span className="text-xs text-[#202020]/70">WhatsApp</span>
              </a>

              {/* Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2"
                onClick={() => setShowShareSheet(false)}
              >
                <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-xs text-[#202020]/70">Facebook</span>
              </a>

              {/* X / Twitter */}
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(event.title)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2"
                onClick={() => setShowShareSheet(false)}
              >
                <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <span className="text-xs text-[#202020]/70">X</span>
              </a>

              {/* Email */}
              <a
                href={`mailto:?subject=${encodeURIComponent(event.title)}&body=${encodeURIComponent('Check out this event: ' + window.location.href)}`}
                className="flex flex-col items-center gap-2"
                onClick={() => setShowShareSheet(false)}
              >
                <div className="w-14 h-14 bg-gray-600 rounded-2xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-xs text-[#202020]/70">Email</span>
              </a>

              {/* Messages */}
              <a
                href={`sms:&body=${encodeURIComponent(event.title + ' - ' + window.location.href)}`}
                className="flex flex-col items-center gap-2"
                onClick={() => setShowShareSheet(false)}
              >
                <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
                  </svg>
                </div>
                <span className="text-xs text-[#202020]/70">Message</span>
              </a>

              {/* More options (native share) */}
              {typeof navigator.share === 'function' && (
                <button
                  onClick={() => {
                    navigator.share({ title: event.title, url: window.location.href, text: event.description?.slice(0, 100) });
                    setShowShareSheet(false);
                  }}
                  className="flex flex-col items-center gap-2"
                >
                  <div className="w-14 h-14 bg-accent-text rounded-2xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </div>
                  <span className="text-xs text-[#202020]/70">More</span>
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Mobile sticky attend button (original style) */}
      {salesActive && !isOwnEvent && createPortal(
        hasExternalTicketUrl ? (
          <div className="md:hidden fixed bottom-16 left-0 right-0 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] z-40">
            <a
              href={event.ticket_url!.startsWith('http') ? event.ticket_url! : `https://${event.ticket_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-4 px-6 bg-accent-text text-white text-base font-semibold rounded-2xl hover:bg-accent-text/90 active:scale-[0.98] transition-all flex items-center justify-between shadow-lg shadow-accent-text/20"
            >
              <span className="font-semibold">Attend</span>
              <span className="font-semibold">
                {event.price === 0 ? t('common.free') : `${sym}${event.price.toFixed(2)}`}
              </span>
            </a>
          </div>
        ) : hasTickets ? (
          <div className="md:hidden fixed bottom-16 left-0 right-0 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] z-40">
            <button
              onClick={handleAttendClick}
              disabled={instantAttendLoading}
              className="w-full py-3 px-4 bg-white rounded-2xl shadow-md hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-between border border-gray-200"
            >
              <div className="flex flex-col items-start">
                <span className="px-3 py-1 rounded-full border border-gray-300 font-semibold text-sm text-gray-600">
                  {event.price === 0 ? t('common.free') : `${sym}${event.price.toFixed(2)}`}
                </span>
                {event.price === 0 && event.max_participants && (
                  <span className="text-xs font-medium mt-1" style={{ color: '#C4714A' }}>
                    {event.max_participants - (event.current_participants || 0)} spots left
                  </span>
                )}
              </div>
              <span className="px-6 py-2 bg-[#8DA847] text-white font-semibold rounded-full">Attend</span>
            </button>
          </div>
        ) : !hasTickets && event.max_participants && salesActive && !isOwnEvent ? (
          <div className="md:hidden fixed bottom-16 left-0 right-0 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] z-40">
            <button
              onClick={handleAttendClick}
              disabled={instantAttendLoading}
              className="w-full py-3 px-4 bg-white rounded-2xl shadow-md hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-between border border-gray-200"
            >
              <div className="flex flex-col items-start">
                <span className="px-3 py-1 rounded-full border border-gray-300 font-semibold text-sm text-gray-600">{t('common.free')}</span>
                {event.max_participants && (
                  <span className="text-xs font-medium mt-1" style={{ color: '#C4714A' }}>
                    {event.max_participants - (event.current_participants || 0)} spots left
                  </span>
                )}
              </div>
              <span className="px-6 py-2 bg-[#8DA847] text-white font-semibold rounded-full">Attend</span>
            </button>
          </div>
        ) : !hasTickets && !event.max_participants && salesActive && !isOwnEvent ? (
          <div className="md:hidden fixed bottom-16 left-0 right-0 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] z-40">
            <button
              onClick={handleAttendClick}
              disabled={instantAttendLoading}
              className="w-full py-3 px-4 bg-white rounded-2xl shadow-md hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-between border border-gray-200 disabled:opacity-60"
            >
              <span className="px-3 py-1 rounded-full border border-gray-300 font-semibold text-sm text-gray-600">{t('common.free')}</span>
              <span className="px-6 py-2 bg-[#8DA847] text-white font-semibold rounded-full">
                {instantAttendLoading ? 'Joining...' : 'Attend'}
              </span>
            </button>
          </div>
        ) : null,
        document.body
      )}

      {/* Desktop sticky action bar with extra options */}
      {salesActive && !isOwnEvent && !showTicketModal && createPortal(
        <div className="hidden md:block fixed left-0 right-0 bottom-4 px-6 z-[60]">
          <div className="max-w-5xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-lg">
            <div className="flex items-center gap-2 p-3.5">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-content/45 leading-none mb-1">
                  {startWeekdayShort}, {formatDate(startDate)} {formatTime(startDate)}
                </p>
                <p className="text-base font-semibold text-content truncate">
                  {event.title}
                </p>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleStickyShare}
                  className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 text-content flex items-center justify-center transition-colors"
                  aria-label="Share event"
                >
                  <Share2 className="h-4.5 w-4.5" />
                </button>

                <div onClick={(e) => e.stopPropagation()} className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <BookmarkButton targetId={event.id} targetType="events" className="!p-0" />
                </div>

                {hasExternalTicketUrl ? (
                  <a
                    href={event.ticket_url!.startsWith('http') ? event.ticket_url! : `https://${event.ticket_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center h-10 px-2.5 bg-white text-content text-sm font-semibold rounded-full border border-gray-300 shadow-sm hover:bg-white transition-colors whitespace-nowrap gap-2"
                  >
                    <span className="px-2 text-sm text-content/70">
                      {event.price === 0 ? t('common.free') : `${sym}${event.price.toFixed(2)}`}
                    </span>
                    <span className="px-3 py-1.5 bg-[#8DA847] text-white rounded-full leading-none">Attend</span>
                  </a>
                ) : (
                  <button
                    onClick={handleAttendClick}
                    disabled={instantAttendLoading}
                    className="inline-flex items-center h-10 px-2.5 bg-white text-content text-sm font-semibold rounded-full border border-gray-300 shadow-sm hover:bg-white transition-colors whitespace-nowrap gap-2"
                  >
                    <span className="px-2 text-sm text-content/70">
                      {event.price === 0 ? t('common.free') : `${sym}${event.price.toFixed(2)}`}
                    </span>
                    <span className="px-3 py-1.5 bg-[#8DA847] text-white rounded-full leading-none">Attend</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      </div>
    </>
  );
}